<template>
  <div
    class="login_background flex justify-center align-center flex-col items-center h-screen w-full bg-no-repeat bg-right"
    :style="{ backgroundImage: `url(${require('images/login_background.png')})` }"
  >
    <div class="flex justify-start flex-col items-center" style="width: 500px">
      <div class="self-center mb-6">
        <logoSvg />
      </div>

      <h2 class="text-2xl font-semibold text-black mb-1">{{ currentStepTitle }}</h2>
      <p class="text-gray-600 text-base mb-8">{{ currentStepDescription }}</p>

      <!-- Step 1: Loading your program -->
      <div class="flex items-center justify-between w-full max-w-md mb-2 py-3 px-4">
        <div class="flex items-center gap-3">
          <div
            class="flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 transition-all duration-300"
            :class="step1Done ? 'border-2 border-coach-green-500 bg-coach-green-500' : ''"
          >
            <div
              v-if="!step1Done"
              class="content-loading-spinner"
            ></div>
            <svg
              v-if="step1Done"
              class="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <span class="font-medium text-black">Loading your program</span>
        </div>
        <div class="text-sm font-medium min-w-[80px] text-right">
          <span v-if="step1Done" class="text-coach-green-500">Done</span>
          <span v-else class="text-coach-green-500 flex items-center gap-1">
            Processing...
          </span>
        </div>
      </div>

      <!-- Step 2: Setting up for your clients -->
      <div class="flex items-center justify-between w-full max-w-md mb-2 py-3 px-4">
        <div class="flex items-center gap-3">
          <div
            class="flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 transition-all duration-300"
            :class="step2Done ? 'border-2 border-coach-green-500 bg-coach-green-500' : ''"
          >
            <div
              v-if="!step2Done"
              class="content-loading-spinner"
            ></div>
            <svg
              v-if="step2Done"
              class="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <span class="font-medium text-black">Setting up for your clients</span>
        </div>
        <div class="text-sm font-medium min-w-[80px] text-right">
          <span v-if="step2Done" class="text-coach-green-500">Done</span>
          <span v-else class="text-coach-green-500 flex items-center gap-1 justify-end">
            Processing...
          </span>
        </div>
      </div>

      <!-- Step 3: Almost ready -->
      <div class="flex items-center justify-between w-full max-w-md mb-2 py-3 px-4">
        <div class="flex items-center gap-3">
          <div
            class="flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 transition-all duration-300"
            :class="step3Done ? 'border-2 border-coach-green-500 bg-coach-green-500' : ''"
          >
            <div
              v-if="!step3Done"
              class="content-loading-spinner"
            ></div>
            <svg
              v-if="step3Done"
              class="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <span class="font-medium text-black">Almost ready</span>
        </div>
        <div class="text-sm font-medium min-w-[80px] text-right">
          <span v-if="step3Done" class="text-coach-green-500">Done</span>
          <span v-else class="text-coach-green-500 flex items-center gap-1 justify-end">
            Processing...
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ProgressBar from '../shared/progress-bar';
import logoSvg from '../../svgs/logoSvg.vue';

const TEMPLATE_LABELS = {
  workout: { functional_strength: 'functional strength', bodybuilding: 'bodybuilding', powerlifting: 'powerlifting', crossfit: 'CrossFit' },
  nutrition: { habit_based: 'habit-based', macro_tracking: 'macro tracking' },
  challenge: { '7_day': '7-day', '4_week': '4-week', '6_week': '6-week' },
};

export default {
  name: 'SignUpContentLoading',
  components: { ProgressBar, logoSvg },
  props: {
    step1Done: { type: Boolean, default: false },
    step2Done: { type: Boolean, default: false },
    step3Done: { type: Boolean, default: false },
    buildType: { type: String, default: '' },
    buildSubtype: { type: String, default: '' },
  },
  computed: {
    step1Description() {
      const label = this.templateLabel;
      return label ? `pulling the ${label} template….` : 'pulling the template….';
    },
    step2Description() {
      return 'adding this to your coaching dashboard….';
    },
    step3Description() {
      return 'finalizing your program….';
    },
    templateLabel() {
      if (!this.buildType || !this.buildSubtype) return '';
      const map = TEMPLATE_LABELS[this.buildType];
      return map ? (map[this.buildSubtype] || this.buildSubtype) : this.buildSubtype;
    },
    currentStepTitle() {
      if (!this.step1Done) return 'Loading your program';
      if (!this.step2Done) return 'Setting up for your clients';
      return 'Almost ready';
    },
    currentStepDescription() {
      if (!this.step1Done) return this.step1Description;
      if (!this.step2Done) return this.step2Description;
      return this.step3Description;
    },
  },
  beforeCreate() {
    document.body.style.backgroundColor = '#FFFFFF';
  },
};
</script>

<style scoped>
.content-loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #e5e7eb;
  border-top-color: #3AB574;
  border-radius: 50%;
  animation: content-spin 0.8s linear infinite;
}
@keyframes content-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
